import React, { useEffect, useRef, useState } from "react";
import { useStates } from "../../../hooks/useStatesAndConstituencies";
import { showError, showSuccess } from "../../../utils/toastUtils";
import sportAPI from "../../../api/admin/sportApi";
import SunEditor from "suneditor-react";
import sopGuideAPI from "../../../api/admin/sopGuideApi";
import DragDropMultiUpload from "../../../layout/admin/partials/DragDropMultiUpload";

function EditDetails({ data, onSuccess }) {
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const editorRef = useRef();
  const handleEditorChange = (content) => {
    setMessage(content);
  };

  const handleDeleteAttachment = async (id) => {
    const payload = {
      sopId: data._id,
      attachmentId: id,
    };

    const res = await sopGuideAPI.deleteAttachment(payload);
    if(res.code === 200) {
      fetchSopDetails(data._id);
    }
  };

  useEffect(() => {
    if (data) {
      fetchSopDetails(data._id);
    }
  }, [data]);

  const fetchSopDetails = async (id) => {
    const res = await sopGuideAPI.getDetails({ id });

    if (res.code === 200) {
      const result = res.data;
      setTitle(result.title);
      setMessage(result.message);
      setUploadedFiles(result.attachments);
    }
  };

  const validate = () => {
    let newErrors = {};

    if (!title.trim()) {
      newErrors.title = "Please enter Title.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (!validate()) return;

      const form = new FormData();
      form.append("id", data._id);
      form.append("title", title);
      form.append("message", message);

      files.forEach((f, i) => {
        form.append("files[]", f);
      });

      const res = await sopGuideAPI.update(form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.code === 200) {
        showSuccess(res.message);
        onSuccess();

        document.getElementById("closeOffcanvasEditBtn").click();
      } else {
        showError(res.message);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      showError(err.message);
    }
  };
  return (
    <>
      <form
        method="post"
        onSubmit={handleSubmit}
        className="vh-100 d-flex justify-content-between flex-column"
      >
        <div className="offcanvas-header">
          <h4 id="offcanvasRightLabel">Edit Details for {data?.name}</h4>
          <button
            type="button"
            id="closeOffcanvasEditBtn"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />
        </div>
        <div className="offcanvas-body">
          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                placeholder="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              {errors.title && (
                <span className="invalid-feedback d-block">{errors.title}</span>
              )}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-12">
              <label className="form-label">Description</label>
              <SunEditor
                getSunEditorInstance={(sunEditor) => {
                  editorRef.current = sunEditor;
                }}
                height="250px"
                setOptions={{
                  buttonList: [
                    ["undo", "redo"],
                    ["font", "fontSize"],
                    ["formatBlock"],
                    ["bold", "underline", "italic", "strike"],
                    ["fontColor", "hiliteColor"],
                    ["align", "list", "table"],
                    ["link", "image", "video"],
                    ["fullScreen", "preview", "print"],
                  ],
                  imageFileInput: true,
                  font: [
                    "Arial",
                    "Comic Sans MS",
                    "Courier New",
                    "Georgia",
                    "Tahoma",
                    "Trebuchet MS",
                    "Verdana",
                  ],
                  fontSize: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36], // include 12px
                  attributesWhitelist: {
                    all: "style class id", // har tag par style/class/id allow
                    table: "cellpadding cellspacing border",
                    img: "src alt width height style",
                    span: "style class",
                  },
                  pasteTagsWhitelist:
                    "p|br|strong|b|i|u|span|div|table|tr|td|th|img|a|ul|ol|li|h1|h2|h3|h4|h5|h6",
                }}
                setContents={message}
                onChange={handleEditorChange}
              />
            </div>
          </div>

          <DragDropMultiUpload
            onChange={(selectedFiles) => setFiles(selectedFiles)}
          />

          {uploadedFiles && uploadedFiles.length > 0 ? (
            uploadedFiles.map((file, index) => (
              <div
                key={`${file.name}-${file.size}-${index}`}
                className="col-12 col-md-6 col-lg-4"
              >
                <div className="border rounded-3 p-2 h-100 d-flex gap-2">
                  <div
                    style={{ width: 56, height: 56 }}
                    className="flex-shrink-0 rounded overflow-hidden bg-white d-flex align-items-center justify-content-center border"
                  >
                    {file.preview ? (
                      <img
                        src={file.preview}
                        alt={file.originalName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        loading="lazy"
                      />
                    ) : (
                      <span className="ti ti-file" aria-hidden="true" />
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div
                      className="fw-semibold text-truncate"
                      title={file.originalName}
                    >
                      {file.originalName}
                    </div>
                    <div className="mt-2 d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={(e) => {
                          handleDeleteAttachment(file._id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <span>No attachments</span>
          )}

          <div className="mt-3 mb-3">
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              {loading ? "Updating Data..." : "Submit"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
}

export default EditDetails;
